import React from 'react';
import PropTypes from 'prop-types';
import './Loader.css';

const Loader = ({
  type = 'spinner',
  size = 'md',
  color = 'primary',
  text,
  textPosition = 'bottom',
  centered = false,
  overlay = false,
  className = '',
  ...props
}) => {
  const baseClass = `loader loader--${size} loader--${color}`;
  const classes = [
    baseClass,
    centered && 'loader--center',
    overlay && 'loader--overlay',
    text && textPosition === 'right' && 'loader--horizontal',
    text && textPosition !== 'right' && 'loader--with-text',
    className
  ].filter(Boolean).join(' ');

  const renderLoader = () => {
    switch (type) {
      case 'dots':
        return (
          <div className="loader--dots">
            <div className="loader__dot"></div>
            <div className="loader__dot"></div>
            <div className="loader__dot"></div>
          </div>
        );
      
      case 'pulse':
        return <div className="loader__pulse"></div>;
      
      case 'bars':
        return (
          <div className="loader--bars">
            <div className="loader__bar"></div>
            <div className="loader__bar"></div>
            <div className="loader__bar"></div>
            <div className="loader__bar"></div>
          </div>
        );
      
      case 'ring':
        return <div className="loader__ring"></div>;
      
      case 'progress':
        return <div className="loader__progress"></div>;
      
      case 'spinner':
      default:
        return <div className="loader__spinner"></div>;
    }
  };

  const content = (
    <>
      {text && textPosition === 'top' && (
        <div className="loader__text">{text}</div>
      )}
      {renderLoader()}
      {text && (textPosition === 'bottom' || textPosition === 'right') && (
        <div className="loader__text">{text}</div>
      )}
    </>
  );

  return (
    <div className={classes} {...props}>
      {content}
    </div>
  );
};

// Специализированные компоненты лоадеров
export const ButtonLoader = ({ size = 'sm', ...props }) => (
  <div className="button-loader">
    <Loader type="spinner" size={size} {...props} />
  </div>
);

export const TableLoader = ({ text = 'Загрузка данных...', ...props }) => (
  <div className="table-loader">
    <Loader type="spinner" size="lg" text={text} {...props} />
  </div>
);

export const CardLoader = ({ text = 'Загрузка...', ...props }) => (
  <div className="card-loader">
    <Loader type="pulse" size="lg" text={text} {...props} />
  </div>
);

export const InlineLoader = ({ text, ...props }) => (
  <div className="inline-loader">
    <Loader type="spinner" size="sm" {...props} />
    {text && <span>{text}</span>}
  </div>
);

export const PageLoader = () => (
  <div className="page-loader">
    <div className="page-loader__bar"></div>
  </div>
);

// Skeleton компоненты
export const SkeletonText = ({ lines = 3, className = '' }) => (
  <div className={`loader__skeleton-container ${className}`}>
    {Array.from({ length: lines }, (_, index) => (
      <div 
        key={index}
        className={`loader__skeleton loader__skeleton--text ${
          index === lines - 1 ? 'loader__skeleton--text-last' : ''
        }`}
      />
    ))}
  </div>
);

export const SkeletonTitle = ({ className = '' }) => (
  <div className={`loader__skeleton loader__skeleton--title ${className}`} />
);

export const SkeletonAvatar = ({ size = 'md', className = '' }) => (
  <div 
    className={`loader__skeleton loader__skeleton--avatar loader__skeleton--${size} ${className}`} 
  />
);

export const SkeletonCard = ({ className = '' }) => (
  <div className={`loader__skeleton loader__skeleton--card ${className}`} />
);

export const SkeletonButton = ({ className = '' }) => (
  <div className={`loader__skeleton loader__skeleton--button ${className}`} />
);

// HOC для состояний загрузки
export const withLoading = (WrappedComponent) => {
  return function WithLoadingComponent({ loading, loadingProps = {}, ...props }) {
    if (loading) {
      return (
        <div className="loading-state">
          <WrappedComponent {...props} />
          <Loader overlay centered {...loadingProps} />
        </div>
      );
    }
    
    return <WrappedComponent {...props} />;
  };
};

// PropTypes
Loader.propTypes = {
  type: PropTypes.oneOf(['spinner', 'dots', 'pulse', 'bars', 'ring', 'progress']),
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  color: PropTypes.oneOf(['primary', 'secondary', 'success', 'muted']),
  text: PropTypes.string,
  textPosition: PropTypes.oneOf(['top', 'bottom', 'right']),
  centered: PropTypes.bool,
  overlay: PropTypes.bool,
  className: PropTypes.string
};

ButtonLoader.propTypes = {
  size: PropTypes.oneOf(['sm', 'md', 'lg'])
};

TableLoader.propTypes = {
  text: PropTypes.string
};

CardLoader.propTypes = {
  text: PropTypes.string
};

InlineLoader.propTypes = {
  text: PropTypes.string
};

SkeletonText.propTypes = {
  lines: PropTypes.number,
  className: PropTypes.string
};

SkeletonTitle.propTypes = {
  className: PropTypes.string
};

SkeletonAvatar.propTypes = {
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  className: PropTypes.string
};

SkeletonCard.propTypes = {
  className: PropTypes.string
};

SkeletonButton.propTypes = {
  className: PropTypes.string
};

export default Loader;